// @ts-nocheck
import { FormEvent, useEffect, useState } from "react";
import { ArrowLeft, Camera, Plus, RefreshCw, Siren, Wrench } from "lucide-react";
import { request } from "./FunctionalApp";
const pretty = (x: string = "") =>
  x
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
async function compressImage(file: File, maxPx = 1400, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = url;
  });
}
async function api(path: string, init: RequestInit = {}) {
  const r = await request(path, init),
    x = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(pretty(x.error || "Unable to update ticket"));
  return x;
}
const post = (p: string, b: any = {}) =>
  api(p, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(b),
  });
export function HelpdeskWorkspace() {
  const [data, setData] = useState<any>({
      tickets: [],
      assets: [],
      jobTypes: [],
    }),
    [selected, setSelected] = useState(""),
    [detail, setDetail] = useState<any>(null),
    [create, setCreate] = useState(false),
    [noteAction, setNoteAction] = useState<any>(null),
    [message, setMessage] = useState("");
  async function load(id = selected) {
    try {
      setData(await api("/api/helpdesk"));
      if (id) setDetail(await api(`/api/helpdesk/${id}`));
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function action(suffix: string, body: any = {}) {
    try {
      const x = await post(`/api/helpdesk/${selected}/${suffix}`, body);
      setMessage(x.number ? `${x.number} created` : "Ticket updated");
      await load(selected);
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  async function submitNoteAction(note:string){await action(noteAction.suffix,{note});setNoteAction(null)}
  if (detail)
    return (
      <>
        <div className="cmms-detail-head">
          <button
            className="secondary"
            onClick={() => {
              setDetail(null);
              setSelected("");
            }}
          >
            <ArrowLeft />
            Helpdesk
          </button>
          <div>
            <span>{detail.ticket.number}</span>
            <h1>{detail.ticket.title}</h1>
            <p>{detail.ticket.asset_path || "No asset selected"}</p>
            <p>
              {detail.ticket.job_type_name || "Helpdesk request"}
              {detail.ticket.contractor ? " · Contractor" : ""}
            </p>
          </div>
          <em className={`help-status h-${detail.ticket.status}`}>
            {pretty(detail.ticket.status)}
          </em>
        </div>
        {message ? <div className="func-admin-note">{message}</div> : null}
        <div className="help-detail">
          <section className="func-panel">
            <h2>Breakdown report</h2>
            <p>{detail.ticket.description || "No description supplied."}</p>
            <dl>
              <div>
                <dt>Priority</dt>
                <dd>{pretty(detail.ticket.priority)}</dd>
              </div>
              <div>
                <dt>Trade</dt>
                <dd>{pretty(detail.ticket.discipline)}</dd>
              </div>
              <div>
                <dt>Reported by</dt>
                <dd>{detail.ticket.reported_by_name}</dd>
              </div>
              <div>
                <dt>Claimed by</dt>
                <dd>{detail.ticket.claimed_by_name || "Unclaimed"}</dd>
              </div>
            </dl>
            <div className="help-actions">
              {detail.ticket.status === "OPEN" && data.canManage ? (
                <button
                  onClick={() =>
                    action("escalate", {
                      note: "Reviewed and escalated to maintenance",
                    })
                  }
                >
                  Escalate to maintenance
                </button>
              ) : null}
              {detail.ticket.status === "ESCALATED" && data.canClaim ? (
                <button onClick={() => action("claim")}>
                  Claim for myself
                </button>
              ) : null}
              {detail.ticket.status === "CLAIMED" ? (
                <>
                  <button onClick={() => setNoteAction({suffix:"production-restored",title:"How production was restored"})}>
                    Production restored
                  </button>
                  <button onClick={() => setNoteAction({suffix:"resolve",title:"Repair resolution details"})}>
                    Resolve repair
                  </button>
                </>
              ) : null}
              {!detail.ticket.work_order_id &&
              ["CLAIMED", "PRODUCTION_RESOLVED_UNCLAIMED"].includes(
                detail.ticket.status,
              ) ? (
                <button onClick={() => action("convert")}>
                  <Wrench />
                  Create work order
                </button>
              ) : null}
              {detail.ticket.status === "PRODUCTION_RESOLVED_UNCLAIMED" ? (
                <button onClick={() => setNoteAction({suffix:"resolve",title:"Permanent repair details"})}>
                  Complete outstanding repair
                </button>
              ) : null}
              {detail.ticket.status === "RESOLVED" && data.canManage ? (
                <button
                  onClick={() =>
                    action("close", { note: "Team leader closure" })
                  }
                >
                  Close ticket
                </button>
              ) : null}
            </div>
            {noteAction ? <ActionNoteForm title={noteAction.title} save={submitNoteAction} cancel={()=>setNoteAction(null)} /> : null}
            {detail.ticket.work_order_id ? (
              <p className="help-linked">
                Linked maintenance work order created.
              </p>
            ) : null}
          </section>
          <section className="func-panel help-events">
            <h2>Ticket history</h2>
            <NoteForm save={(body: string) => action("notes", { body })} />
            {detail.events.map((e: any) => (
              <article key={e.id}>
                <b>{pretty(e.event)}</b>
                <small>
                  {e.actor_name} · {new Date(e.created_at).toLocaleString()}
                </small>
                {e.body ? <p>{e.body}</p> : null}
              </article>
            ))}
          </section>
          {!["RESOLVED","CLOSED"].includes(detail.ticket.status) && (
            <section className="func-panel help-photos">
              <div className="func-table-head">
                <h2>Photos</h2>
                <small style={{marginLeft:"auto",color:"var(--muted)",fontSize:11}}>
                  Temporary — deleted automatically when ticket resolves
                </small>
              </div>
              {(detail.attachments || []).length > 0 && (
                <div className="help-photo-grid">
                  {detail.attachments.map((a: any) => (
                    <div key={a.id} className="help-photo-item">
                      <img src={a.url} alt={a.name} onClick={() => window.open(a.url, "_blank")} />
                      <button className="help-photo-delete" title="Delete" onClick={async () => {
                        await api(`/api/helpdesk/${detail.ticket.id}/attachments/${a.id}`, { method: "DELETE" });
                        load(selected);
                      }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              {(detail.attachments || []).length < 10 && (
                <label className="help-photo-upload">
                  <Camera /> Add photo
                  <input type="file" accept="image/*" capture="environment" hidden multiple onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    for (const file of files) {
                      const compressed = await compressImage(file, 1400, 0.82);
                      await api(`/api/helpdesk/${detail.ticket.id}/attachments`, {
                        method: "POST",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify({ name: file.name, url: compressed, mimeType: file.type }),
                      });
                    }
                    load(selected);
                    e.target.value = "";
                  }} />
                </label>
              )}
              {(detail.attachments || []).length === 0 && <p className="func-empty">No photos yet.</p>}
            </section>
          )}
        </div>
      </>
    );
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Maintenance Helpdesk</h1>
          <p>
            {data.isBasicUser
              ? "Raise a problem report and track tickets from your department."
              : "Report production problems, escalate them and retain the complete response history."}
          </p>
        </div>
        <button onClick={() => setCreate(!create)}>
          <Plus />
          Report a problem
        </button>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      {create ? (
        <TicketForm
          assets={data.assets}
          jobTypes={data.jobTypes}
          tickets={data.tickets}
          saved={async (id) => {
            setCreate(false);
            await load();
            setSelected(id);
            setDetail(await api(`/api/helpdesk/${id}`));
          }}
        />
      ) : null}
      <section className="func-panel help-list">
        <div className="func-table-head">
          <h2>Tickets</h2>
          <button className="secondary" onClick={() => load()}>
            <RefreshCw />
            Refresh
          </button>
        </div>
        {data.tickets.map((t: any) => (
          <button
            key={t.id}
            onClick={async () => {
              setSelected(t.id);
              setDetail(await api(`/api/helpdesk/${t.id}`));
            }}
          >
            <Siren />
            <span>
              <b>
                {t.number} · {t.title}
              </b>
              <small>
                {t.asset_path || "No asset"} ·{" "}
                {t.job_type_name || "Helpdesk request"}
                {t.contractor ? " · Contractor" : ""} · {t.reported_by_name}
              </small>
            </span>
            <em className={`help-priority p-${t.priority}`}>
              {pretty(t.priority)}
            </em>
            <span>
              <b>{pretty(t.status)}</b>
              <small>
                {t.claimed_by_name || "Unclaimed"} · {pretty(t.discipline)}
              </small>
            </span>
          </button>
        ))}
      </section>
    </>
  );
}
function ActionNoteForm({title,save,cancel}:any){const[value,setValue]=useState("");return <form className="help-action-note" onSubmit={e=>{e.preventDefault();if(value.trim())save(value.trim())}}><label>{title}<textarea autoFocus required minLength={4} rows={5} value={value} onChange={e=>setValue(e.target.value)} placeholder="Record the work completed, checks made, machine condition and any remaining action." /></label><div><button type="submit">Save and continue</button><button type="button" className="secondary" onClick={cancel}>Cancel</button></div></form>}
function TicketForm({ assets, jobTypes, tickets, saved }: any) {
  const [f, setF] = useState({
      title: "",
      description: "",
      assetId: "",
      priority: "PRODUCTION_CAN_RUN",
      discipline: "GENERAL",
      jobTypeId: jobTypes[0]?.id || "",
    }),
    [error, setError] = useState("");

  const duplicates = f.assetId
    ? (tickets || []).filter((t: any) =>
        t.asset_id === f.assetId && !["RESOLVED","CLOSED"].includes(t.status))
    : [];

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const x = await post("/api/helpdesk", f);
      saved(x.id);
    } catch (e: any) {
      setError(e.message);
    }
  }
  return (
    <section className="func-panel help-form">
      <h2>Report maintenance problem</h2>
      <form onSubmit={submit}>
        <label>
          Problem title
          <input
            required
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </label>
        <label>
          Affected asset
          <select
            value={f.assetId}
            onChange={(e) => setF({ ...f, assetId: e.target.value })}
          >
            <option value="">Choose asset…</option>
            {assets.map((a: any) => (
              <option key={a.id} value={a.id}>
                {a.path}
              </option>
            ))}
          </select>
        </label>
        {duplicates.length > 0 && (
          <div className="help-duplicate-warning">
            <b>⚠ Open ticket{duplicates.length > 1 ? "s" : ""} already exist for this asset:</b>
            {duplicates.map((t: any) => (
              <div key={t.id} className="help-duplicate-item">
                <span>{t.number}: {t.title}</span>
                <em className={`help-status h-${t.status}`}>{t.status.replaceAll("_"," ")}</em>
              </div>
            ))}
            <p>Check these before raising a new ticket to avoid duplicates.</p>
          </div>
        )}
        <label>
          Request type
          <select
            required
            value={f.jobTypeId}
            onChange={(e) => setF({ ...f, jobTypeId: e.target.value })}
          >
            {jobTypes.map((type: any) => (
              <option key={type.id} value={type.id}>
                {type.name}
                {type.contractor ? " — contractor" : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          Production impact
          <select
            value={f.priority}
            onChange={(e) => setF({ ...f, priority: e.target.value })}
          >
            <option value="NORMAL">Normal</option>
            <option value="PRODUCTION_CAN_RUN">Production can run</option>
            <option value="PRODUCTION_STOPPED">Production stopped</option>
          </select>
        </label>
        <label>
          Trade
          <select
            value={f.discipline}
            onChange={(e) => setF({ ...f, discipline: e.target.value })}
          >
            <option value="GENERAL">General / unknown</option>
            <option value="MECHANICAL">Mechanical</option>
            <option value="ELECTRICAL">Electrical</option>
          </select>
        </label>
        <label className="wide">
          Description
          <textarea
            required
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </label>
        {error ? <p>{error}</p> : null}
        <button>Submit ticket</button>
      </form>
    </section>
  );
}
function NoteForm({ save }: any) {
  const [value, setValue] = useState("");
  return (
    <form
      className="help-note"
      onSubmit={(e) => {
        e.preventDefault();
        if (value) {
          save(value);
          setValue("");
        }
      }}
    >
      <input
        placeholder="Add ticket note"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button>Add note</button>
    </form>
  );
}
